/**
 * TryQuiPus — i18n dictionary (English / Spanish)
 *
 * Terminology standard:
 *   - IFRS (IAS 1, IAS 7, IAS 16) for financial statement names
 *   - US GAAP used as secondary reference where IFRS differs minimally
 *   - SUNAT acronyms (SIRE, PLE, IGV, RER, MYPE) preserved as-is
 *
 * Usage:
 *   import { T } from '../i18n';
 *   <h1>{T.balance}</h1>
 *
 * To switch the platform default, flip DEFAULT_LANG below.
 */

export type Lang = 'en' | 'es';

export const DEFAULT_LANG: Lang = 'es';

// Import the extended dictionary's t function as fallback for dotted keys
import { t as dictT } from './dictionary';

const dict = {
  // ── Financial Statements (IAS 1) ────────────────────────────────────
  balance:       { en: 'Statement of Financial Position', es: 'Estado de Situación Financiera' },
  pyg:           { en: 'Income Statement',                es: 'Estado de Resultados' },
  //               IAS 1 full name: Statement of Profit or Loss and Other Comprehensive Income
  //               Abbreviated to "Income Statement" (US GAAP / common usage)
  flujo:         { en: 'Statement of Cash Flows',         es: 'Flujo de Efectivo' },
  //               IAS 7: Statement of Cash Flows — Direct Method
  patrimonio:    { en: 'Statement of Changes in Equity',  es: 'Estado de Cambios en el Patrimonio' },

  // ── Accounting Cycle ────────────────────────────────────────────────
  libroDiario:   { en: 'General Journal',                 es: 'Libro Diario' },
  //               Libro Diario = General Journal (chronological entries)
  libroMayor:    { en: 'General Ledger',                  es: 'Libro Mayor' },
  //               Libro Mayor = General Ledger (account-level accumulation)
  journals:      { en: 'Journal Entries',                 es: 'Asientos Contables' },
  //               Asiento Contable = Journal Entry (IAS / US GAAP standard)
  planCuentas:   { en: 'Chart of Accounts (COA)',         es: 'Plan de Cuentas (PCGE)' },
  cierre:        { en: 'Month-End / Year-End Close',      es: 'Cierre Mensual / Anual' },
  //               "Closing the books" — standard US GAAP / IFRS operational term
  conciliar:     { en: 'Reconcile',                       es: 'Conciliar' },
  devengar:      { en: 'Accrue',                          es: 'Devengar' },
  provisionar:   { en: 'Provision (Accrue Liability)',    es: 'Provisionar' },
  auditar:       { en: 'Audit',                           es: 'Auditar' },

  // ── Specific Accounts ───────────────────────────────────────────────
  cuentasCobrar: { en: 'Accounts Receivable (AR)',        es: 'Cuentas por Cobrar' },
  cuentasPagar:  { en: 'Accounts Payable (AP)',           es: 'Cuentas por Pagar' },
  activosFijos:  { en: 'Fixed Assets (PPE)',              es: 'Activos Fijos' },
  //               IAS 16: Property, Plant and Equipment (PPE)
  patrimonio2:   { en: 'Equity',                         es: 'Patrimonio' },
  ingresos:      { en: 'Revenue',                        es: 'Ingresos' },
  gastos:        { en: 'Expenses',                       es: 'Gastos' },
  utilidad:      { en: 'Net Income',                     es: 'Utilidad Neta' },
  //               IAS 1 uses "Profit for the period"; "Net Income" = US GAAP equivalent
  margen:        { en: 'Operating Margin',               es: 'Margen Operativo' },
  reserva:       { en: 'Tax Reserve',                    es: 'Reserva de Impuestos' },

  // ── VAT / IGV (Peruvian local tax — maintain IGV acronym) ───────────
  igv:           { en: 'VAT (IGV)',                      es: 'IGV' },
  //               IGV = Impuesto General a las Ventas — equivalent to VAT
  igvDebito:     { en: 'Output VAT',                    es: 'IGV Débito Fiscal' },
  //               Output VAT = VAT charged on sales
  igvCredito:    { en: 'Input VAT',                     es: 'IGV Crédito Fiscal' },
  //               Input VAT = VAT paid on purchases (recoverable)
  igvPagar:      { en: 'VAT Payable',                   es: 'IGV por Pagar' },
  igvSaldoFavor: { en: 'VAT Credit Balance',            es: 'Saldo a Favor IGV' },

  // ── SUNAT Peruvian Systems (siglas preserved) ────────────────────────
  sire:          { en: 'SIRE – Electronic Purchase & Sales Records', es: 'SIRE – Sistema Integrado de Registros Electrónicos' },
  ple:           { en: 'PLE – Electronic Book System',               es: 'PLE – Programa de Libros Electrónicos' },
  compras:       { en: 'Purchase Register 8.1 (PLE)',                es: 'Registro de Compras 8.1' },
  ventas:        { en: 'Sales Register 14.1 (PLE)',                  es: 'Registro de Ventas 14.1' },
  tributaria:    { en: 'Tax Compliance · SIRE · PLE',               es: 'Cumplimiento Tributario' },
  impuestos:     { en: 'VAT (IGV) Dashboard',                        es: 'Panel de impuestos' },

  // ── Regimes (SUNAT) ─────────────────────────────────────────────────
  nrus:    { en: 'NRUS – New Single Tax Regime',     es: 'Nuevo RUS' },
  rer:     { en: 'RER – Special Tax Regime',         es: 'Régimen Especial de Renta' },
  mype:    { en: 'MYPE Tributario – SME Tax Regime', es: 'MYPE Tributario' },
  general: { en: 'General Tax Regime',               es: 'Régimen General' },

  // ── Pages ───────────────────────────────────────────────────────────
  categorizer:   { en: 'AI Account Categorizer',          es: 'Maestro Contribuyentes' },
  accountant:    { en: 'Accountant Console',              es: 'Consola del Contador' },
  setup:         { en: 'Company Profile',                 es: 'Perfil de Empresa' },
  processing:    { en: 'Process Entries',                 es: 'Procesar Asientos' },
  upload:        { en: 'Upload Bank Statement',           es: 'Cargar Extracto Bancario' },
  export_:       { en: 'Export',                          es: 'Exportar' },
  biblioteca:    { en: 'Library',                         es: 'Biblioteca' },
  baseLegal:     { en: 'SUNAT Legal Library',             es: 'Base Legal SUNAT' },
  normas:        { en: 'IFRS & Standards',                es: 'Normas & NIIFs' },
  xmlParser:     { en: 'XML Invoice Parser',              es: 'Parser XML Facturas' },
  dashboard:     { en: 'KPI Dashboard',                   es: 'Dashboard KPI' },

  // ── KPI Dashboard labels ─────────────────────────────────────────────
  totalRevenue:  { en: 'Total Revenue',      es: 'Ingresos Totales' },
  totalExpenses: { en: 'Total Expenses',     es: 'Gastos Totales' },
  netIncome:     { en: 'Net Income',         es: 'Utilidad Neta' },
  taxReserve:    { en: 'Tax Reserve',        es: 'Reserva Impuestos' },
  revenueAcct:   { en: 'Revenue Account (70)',  es: 'Saldo Acreedor (70)' },
  expenseAcct:   { en: 'Expense Account (60/63)', es: 'Saldo Deudor (60/63)' },
  taxesPayable:  { en: 'Taxes Payable',      es: 'Tributos por Pagar' },
  operatingMargin:{ en: 'Operating Margin',  es: 'Margen Operativo' },

  // ── Common labels ───────────────────────────────────────────────────
  period:        { en: 'Period',        es: 'Período' },
  company:       { en: 'Company',       es: 'Empresa' },
  liveDB:        { en: 'Live',          es: 'Desde la base de datos' },
  save:          { en: 'Save',          es: 'Guardar' },
  cancel:        { en: 'Cancel',        es: 'Cancelar' },
  search:        { en: 'Search',        es: 'Buscar' },
  export:        { en: 'Export',        es: 'Exportar' },
  noData:        { en: 'No data for this period', es: 'Sin datos para este período' },
  vouchers:      { en: 'Tax Documents', es: 'Comprobantes' },
  noCompany:     { en: 'No company',    es: 'Sin empresa' },
  refresh:       { en: 'Refresh',       es: 'Actualizar' },
  active:        { en: 'Active',        es: 'Vigente' },
  cancelled:     { en: 'Cancelled',     es: 'Anulado' },
  allActive:     { en: 'All active',    es: 'Todos vigentes' },
  records:       { en: 'records',       es: 'registros' },
  of:            { en: 'of',            es: 'de' },

  // ── Ventas KPI labels ────────────────────────────────────────────────
  totalSales:    { en: 'Total Sales',               es: 'Total Ventas' },
  taxableBase:   { en: 'Taxable Base',              es: 'Base Imponible' },
  voucherCount:  { en: 'No. of Vouchers',           es: 'N° Comprobantes' },
  taxed18:       { en: 'Taxed 18%',                 es: 'Gravada 18%' },
  chargedToClients: { en: 'VAT charged to clients', es: 'IGV cobrado a clientes' },

  // ── Journal Manual form ──────────────────────────────────────────────
  template:      { en: 'Template',              es: 'Plantilla' },
  newEntry:      { en: 'New Entry',             es: 'Nuevo Asiento' },
  glosaDesc:     { en: 'Description / Memo *',  es: 'Glosa / Descripción *' },
  date:          { en: 'Date *',                es: 'Fecha *' },
  accountCode:   { en: 'Account',               es: 'Cuenta' },
  accountName:   { en: 'Account Name',          es: 'Nombre cuenta' },
  lineGlosa:     { en: 'Line Memo',             es: 'Glosa línea' },
  debit:         { en: 'Debit (S/)',            es: 'Debe (S/)' },
  credit:        { en: 'Credit (S/)',           es: 'Haber (S/)' },

  // ── CierreDual form ──────────────────────────────────────────────────
  taxRegime:     { en: 'Tax Regime',              es: 'Régimen Tributario' },
  workerShare:   { en: 'Worker Profit Share',     es: 'Participación Trabajadores' },
  nonDeductible: { en: 'Non-deductible Expenses', es: 'Gastos No Deducibles (Adiciones)' },
  closeParams:   { en: 'Closing Parameters',      es: 'Parámetros de Cierre' },

  // ── Legal Navigation (NormasLegales) ────────────────────────────────
  normasCenter:   { en: 'Legal Research Center',            es: 'NIC y Normas' },
  allArticlesLbl: { en: 'All Articles',                     es: 'Todos los artículos' },
  rulingsLbl:     { en: 'Rulings',                          es: 'Pronunciamientos' },
  taxFramework:   { en: 'Tax Framework',                    es: 'Marco Tributario' },
  accountingFw:   { en: 'Accounting Framework',             es: 'Marco Contable' },
  articlesLbl:    { en: 'articles',                         es: 'artículos' },
  pronounceLbl:   { en: 'pronouncements',                   es: 'pronunciamientos' },
  noResults:      { en: 'No results found',                 es: 'Sin resultados' },
  viewAllLegal:   { en: 'View all articles',                es: 'Ver todos los artículos' },
  hide:           { en: 'Hide',                             es: 'Ocultar' },
  view:           { en: 'View',                             es: 'Ver' },
  sections:       { en: 'sections',                         es: 'incisos' },
  seeAlso:        { en: 'See also:',                        es: 'Ver también:' },
  normsApplied:   { en: 'Applied norms:',                   es: 'Normas:' },
  officialLeg:    { en: 'SUNAT — Official Legislation',     es: 'SUNAT — Legislación oficial' },
  searchLegal:    { en: 'Search article, number, concept…', es: 'Buscar artículo, número, concepto…' },
  taxImplication: { en: 'Tax & Accounting Implication',     es: 'Implicancia contable-tributaria' },

  // ── Table column headers ─────────────────────────────────────────────
  colDate:        { en: 'Date',             es: 'Fecha' },
  colDesc:        { en: 'Description',      es: 'Descripción' },
  colAccount:     { en: 'Account',          es: 'Cuenta' },
  colAccountName: { en: 'PCGE Name',        es: 'Nombre PCGE' },
  colBaseImp:     { en: 'Taxable Amt (S/)', es: 'Base Imp. (S/)' },
  colIgv:         { en: 'VAT (S/)',         es: 'IGV (S/)' },
  colTotal:       { en: 'Total (S/)',       es: 'Total (S/)' },
  colDoc:         { en: 'Doc',              es: 'Doc' },
  colStatus:      { en: 'Status',           es: 'Estado' },

  // ── Compras / Ventas UI ──────────────────────────────────────────────
  viewSummary:    { en: 'Summary view',     es: 'Vista resumida' },
  viewPle42:      { en: 'PLE 42-col view',  es: 'Vista PLE 42 cols' },
  viewPle34:      { en: 'PLE 35-col view',  es: 'Vista PLE 35 cols' },
  cuentaPcge:     { en: 'PCGE Account',     es: 'Cuenta PCGE' },
  learnForRuc:    { en: 'Learn for RUC',    es: 'Aprender para RUC' },
  dropFilesHere:  { en: 'Drop files here',  es: 'Suelta los archivos aquí' },
  dragFilesHere:  { en: 'Drag your XML and PDF here', es: 'Arrastra tus XML y PDF aquí' },

  // ── Ventas / Compras — status and action labels ──────────────────────
  vigente:        { en: 'Active',           es: 'Vigente' },
  anulado:        { en: 'Cancelled',        es: 'Anulado' },
  desdeDB:        { en: 'Live DB',          es: 'Desde la base de datos' },
  actualizar:     { en: 'Refresh',          es: 'Actualizar' },
  exportarPdf:    { en: 'Export PDF',       es: 'Exportar PDF' },
  imprimir:       { en: 'Print',            es: 'Imprimir' },
  excelPle:       { en: 'Excel PLE',        es: 'Excel PLE' },
  vistaPle34:     { en: 'PLE 34-col view',  es: 'Vista PLE 34' },
  vistaResumen:   { en: 'Summary View',     es: 'Vista Resumen' },
  sinVentas:      { en: 'No sales for this period', es: 'Sin ventas para este período' },
  sinCompras:     { en: 'No purchases for this period', es: 'Sin compras para este período' },
  sinResultados:  { en: 'No results for',   es: 'Sin resultados para' },

  // ── Search placeholders ──────────────────────────────────────────────
  searchByDateDesc:   { en: 'Search by date, description, series or sequential…', es: 'Buscar por Fecha, Descripción, Serie o Correlativo…' },
  searchByGlosaCuo:   { en: 'Search by memo, CUO or PCGE account…',               es: 'Buscar por Glosa, CUO o Cuenta PCGE…' },
  searchByRucCompany: { en: 'Search by RUC or company…',                          es: 'Buscar por RUC o empresa...' },
  searchByRucOrName:  { en: 'Search by RUC or company name…',                     es: 'Buscar por RUC o razón social…' },

  // ── General Journal (LibroDiario) column headers ─────────────────────
  colCuo:         { en: 'CUO',              es: 'CUO' },
  colGlosa:       { en: 'Memo / Line',      es: 'Glosa / Línea' },
  colNroDoc:      { en: 'Doc. No.',         es: 'Nº documento' },
  colDenom:       { en: 'PCGE Name',        es: 'Denominación' },
  colDebit:       { en: 'Debit (S/)',       es: 'Debe (S/)' },
  colCredit:      { en: 'Credit (S/)',      es: 'Haber (S/)' },

  // ── LibroDiario — footer / integrity ────────────────────────────────
  doubleEntryOk:  { en: 'Double-Entry OK',  es: 'Partida Doble OK' },
  discrepancy:    { en: 'Discrepancy!',     es: '¡Discrepancia!' },
  snapshotOk:     { en: 'Snapshot ✓',       es: 'Snapshot ✓' },
  snapshotDiff:   { en: 'Snapshot differs', es: 'Snapshot difiere' },
  annulled:       { en: 'Voided (audit trail)', es: 'Anulado (registro de auditoría)' },
  logicalDelete:  { en: 'Logical delete',   es: 'Eliminado lógico' },
  unbalanced:     { en: 'Unbalanced',       es: 'Sin cuadrar' },

  // ── LibroDiario — empty / drill-down states ──────────────────────────
  noEntriesFor:   { en: 'No entries for',   es: 'Sin asientos para' },
  drillDownFrom:  { en: 'Drill-down from Statement of Financial Position — account', es: 'Drill-down desde Balance General — cuenta' },
  editFilterBelow:{ en: 'Edit the filter below to expand the search', es: 'Edita el filtro abajo para ampliar la búsqueda' },

  // ── General Ledger (LibroMayor) ──────────────────────────────────────
  libroMayorTitle: { en: 'General Ledger',  es: 'Libro Mayor' },
  noMovements:    { en: 'No movements for this period', es: 'Sin movimientos para este período' },
  activeAccounts: { en: 'active accounts',  es: 'cuentas activas' },
  movements:      { en: 'movements',        es: 'movimientos' },
  cumulativeBalance: { en: 'Cumulative balance', es: 'Saldo acum.' },
  debitBalance:   { en: 'Debit balance',    es: 'Saldo deudor' },
  creditBalance:  { en: 'Credit balance',   es: 'Saldo acreedor' },
  balance2:       { en: 'Balance',          es: 'Saldo' },
  totals:         { en: 'Totals',           es: 'Totales' },

  // ── Setup / Company Profile ──────────────────────────────────────────
  companyProfile: { en: 'Company Profile',  es: 'Perfil de la empresa' },
  configureCompany: { en: 'Configure your company to start operating', es: 'Configura tu empresa para comenzar a operar' },
  market:         { en: 'Market',           es: 'Mercado' },
  companyName:    { en: 'Company Name',     es: 'Razón Social' },
  accountingPeriod: { en: 'Accounting period', es: 'Período contable' },
  taxRegimeLabel: { en: 'Tax regime',       es: 'Régimen tributario' },
  saveAndSync:    { en: 'Save & Sync',      es: 'Guardar y Sincronizar' },
  continueUpload: { en: 'Continue → Upload files', es: 'Continuar → Cargar archivos' },
  savedSuccess:   { en: 'Saved. All modules reflect the changes.', es: 'Guardado. Todos los módulos reflejan los cambios.' },
  securityBackup: { en: 'Security & Backup', es: 'Seguridad & Backup' },
  exportBackup:   { en: 'Export Backup',    es: 'Exportar Backup' },
  importBackup:   { en: 'Import Backup',    es: 'Importar Backup' },
  importHistory:  { en: 'Import Classification History', es: 'Importar Historial de Clasificaciones' },
  selectFile:     { en: 'Select file',      es: 'Seleccionar archivo' },
  processing2:    { en: 'Processing…',      es: 'Procesando...' },
  mappingsLearned:{ en: 'mappings learned', es: 'mapeos aprendidos' },
  comingSoon:     { en: 'Coming soon',      es: 'Próximamente' },

  // ── Cash Flow (FlujoCaja) ────────────────────────────────────────────
  cashFlowTitle:  { en: 'Statement of Cash Flows',  es: 'Flujo de Efectivo' },
  directMethod:   { en: 'Direct Method · IAS 7',    es: 'Método Directo · NIC 7' },
  operatingActivities:  { en: 'Operating Activities',    es: 'Actividades de Operación' },
  investingActivities:  { en: 'Investing Activities',    es: 'Actividades de Inversión' },
  financingActivities:  { en: 'Financing Activities',    es: 'Actividades de Financiamiento' },
  netCashChange:  { en: 'Net Change in Cash',       es: 'Variación Neta de Caja' },
  estimatedAmounts: { en: 'Amounts are estimated from registered invoices. For REAL data per IAS 7, create journal entries with 10x accounts in Manual Entries.', es: 'Los montos se estiman desde las facturas registradas. Para datos REAL conforme a NIC 7, crea asientos con cuentas 10x en Asientos Manuales.' },
  noMovementsRegistered: { en: 'No movements registered', es: 'Sin movimientos registrados' },
  subtotal:       { en: 'Subtotal',             es: 'Subtotal' },
  concept:        { en: 'Concept',              es: 'Concepto' },
  account:        { en: 'Account',              es: 'Cuenta' },
  amount:         { en: 'Amount',               es: 'Importe' },
  source:         { en: 'Source',               es: 'Fuente' },
  real:           { en: 'REAL',                 es: 'REAL' },
  estimated:      { en: 'ESTIMATED',            es: 'ESTIMADO' },
  calculatedFrom: { en: 'Calculated from 10x accounts · Direct Method · IAS 7', es: 'Calculado desde asientos 10x · Método Directo · NIC 7' },
  estimatedFromInvoices: { en: 'Estimated from registered invoice amounts', es: 'Estimado desde importes de facturas registradas' },

  // ── IGV / Tax Dashboard (ImpuestosIGV) ──────────────────────────────
  selectCompanyForTax: { en: 'Select a company to view the tax summary.', es: 'Selecciona una empresa para ver el resumen de impuestos.' },
  vatPayableLabel: { en: 'VAT Payable',         es: 'IGV a Pagar' },
  vatCreditLabel:  { en: 'VAT Credit Balance',  es: 'Saldo a Favor' },
  vatBalanceLabel: { en: 'VAT Balance',         es: 'IGV Balance' },
  debitGtCredit:   { en: 'Debit > Credit',      es: 'Débito > Crédito' },
  creditGtDebit:   { en: 'Credit > Debit',      es: 'Crédito > Débito' },
  balanced:        { en: 'Balanced',            es: 'Equilibrado' },
  vouchers2:       { en: 'Vouchers',            es: 'Comprobantes' },
  dueDate:         { en: 'Due',                 es: 'Vence' },
  fixedQuota:      { en: 'Fixed Quota',         es: 'Cuota Fija' },
  estimatedPayment:{ en: 'Estimated Payment',   es: 'Pago a Cuenta Est.' },
  seeQuotas:       { en: 'See quotas →',        es: 'Ver cuotas →' },
  legalRef:        { en: 'Legal ref:',          es: 'Ref. legal:' },
  estimatedDisclaimer: { en: 'Estimated calculation — verify with your accountant', es: 'Cálculo estimado — verificar con tu contador' },
  totalEstimatedPdt: { en: 'Total estimated to declare in PDT 621', es: 'Total estimado a declarar en PDT 621' },
  salesRevenue:    { en: 'Sales / Revenue',     es: 'Ventas / Ingresos' },
  purchasesExpenses: { en: 'Purchases / Expenses', es: 'Compras / Gastos' },
  noSalesThisPeriod: { en: 'No sales in this period', es: 'Sin ventas en este período' },
  noPurchasesThisPeriod: { en: 'No purchases in this period', es: 'Sin compras en este período' },
  totalRecords:    { en: 'Total',               es: 'Total' },
  description:     { en: 'Description',         es: 'Descripción' },
  base:            { en: 'Base',                es: 'Base' },

  // ── Audit / Accounting Health ────────────────────────────────────────
  auditDashboard:  { en: 'Audit Health Dashboard', es: 'Dashboard de Auditoría' },
  accountingHealthReport: { en: 'Accounting Health Report', es: 'Reporte de Salud Contable' },
  scoreFormula:    { en: 'Score = 100 − error-weighted penalty', es: 'Score = 100 − penalización por errores ponderados' },
  noTransactionsPeriod: { en: 'No transactions in period — load data or inject stress test.', es: 'Sin transacciones en el período — carga datos o inyecta stress test.' },
  doubleEntry:     { en: 'Double-Entry',        es: 'Partida Doble' },
  igvConsistency:  { en: 'VAT Consistency (18%)', es: 'Consistencia IGV (18%)' },
  duplicateNumbers:{ en: 'Duplicate Numbering', es: 'Numeración Duplicada' },
  vendorClassification: { en: 'Vendor Classification', es: 'Clasificación Proveedores' },
  allOk:           { en: 'All clear',           es: 'Todo en orden' },
  problemsFound:   { en: 'problem(s)',           es: 'problema(s)' },
  vendorsToClassify: { en: 'Vendors to classify', es: 'Proveedores a clasificar' },
  goToUpload:      { en: 'Go to Upload → classify before next sync', es: 'Ir a Upload → clasificar antes de la próxima sync' },
  activityLog:     { en: 'Activity Log — Recent Classifications', es: 'Activity Log — Clasificaciones Recientes' },
  noClassifications: { en: 'No classifications registered. Upload XMLs or inject stress test.', es: 'Sin clasificaciones registradas. Sube XMLs o inyecta stress test.' },
  stressTest:      { en: 'Stress Test — 500 Record Injection', es: 'Stress Test — Inyección de 500 Registros' },
  stressDesc:      { en: 'Intentional errors: wrong VAT, duplicates, imbalances', es: 'Genera errores intencionales: IGV incorrecto, duplicados, descuadres' },
  inject500:       { en: 'Inject 500 records',  es: 'Inyectar 500 registros' },
  purgeTestData:   { en: 'Purge test data',     es: 'Purgar datos de prueba' },
  selectCompanyFirst: { en: 'Select a company first', es: 'Selecciona una empresa primero' },
  exportPdf:       { en: 'Export PDF',          es: 'Exportar PDF' },
  generating:      { en: 'Generating…',         es: 'Generando…' },
  totalInserted:   { en: 'Total inserted',      es: 'Total insertados' },
  cleanRecords:    { en: 'Clean records',       es: 'Registros limpios' },
  igvErrors:       { en: 'Wrong VAT',           es: 'IGV incorrecto' },
  duplicates:      { en: 'Duplicates',          es: 'Duplicados' },
  pendingMapping:  { en: 'Pending (mapping)',   es: 'Pendientes (mapping)' },

  // ── Upload ───────────────────────────────────────────────────────────
  uploadXmlPdf:    { en: 'Upload XML / PDF invoices', es: 'Cargar XML / PDF facturas' },
  syncToJournal:   { en: 'Sync to General Journal',  es: 'Sincronizar al Libro Diario' },
  destinationEntries: { en: 'Destination Entries',  es: 'Asientos de Destino' },
  loadingFiles:    { en: 'Loading files…',       es: 'Cargando archivos…' },
  readyToSync:     { en: 'Ready to sync',        es: 'Listo para sincronizar' },
  synchronized:    { en: 'Synchronized',         es: 'Sincronizado' },
  syncError:       { en: 'Sync error',           es: 'Error de sincronización' },
  noFilesSelected: { en: 'No files selected',    es: 'Sin archivos seleccionados' },
  dropZoneHint:    { en: 'Drop XML or PDF files here, or click to select', es: 'Suelta archivos XML o PDF aquí, o haz clic para seleccionar' },

  // ── JournalManual ────────────────────────────────────────────────────
  manualEntries:   { en: 'Manual Journal Entries', es: 'Asientos Manuales Registrados' },
  noManualEntries: { en: 'No manual entries registered. Entries you save will appear here.', es: 'No hay asientos manuales registrados. Los asientos que guardes aparecerán aquí.' },
  addLine:         { en: 'Add line',            es: 'Agregar línea' },
  clearForm:       { en: 'Clear',               es: 'Limpiar' },
  saveEntry:       { en: 'Save entry',          es: 'Guardar asiento' },
  saving:          { en: 'Saving…',             es: 'Guardando…' },
  entrySaved:      { en: 'Entry saved successfully.', es: 'Asiento guardado correctamente.' },
  periodClosed:    { en: 'Period closed. No new entries can be recorded.', es: 'Período cerrado. No se pueden registrar nuevos asientos.' },
  invalidDoubleEntry: { en: 'INVALID DOUBLE-ENTRY — entry cannot be saved', es: 'PARTIDA DOBLE INVÁLIDA — El asiento NO se puede guardar' },
  doubleEntryOk2:  { en: 'Double-entry balanced',  es: 'Partida doble cuadrada' },
  adjustImports:   { en: 'Adjust amounts until Debit = Credit.', es: 'Ajusta los importes hasta que Debe = Haber.' },
  deleteEntryConfirm: { en: 'Delete this entry? This action cannot be undone.', es: '¿Eliminar este asiento? Esta acción no se puede deshacer.' },
  balanced2:       { en: 'Balanced',            es: 'Cuadrado' },
  pending:         { en: 'Pending',             es: 'Pendiente' },
  delete:          { en: 'Delete entry',        es: 'Eliminar asiento' },

  // ── CategorizadorRuc ─────────────────────────────────────────────────
  addVendor:       { en: 'Add Vendor',          es: 'Agregar Proveedor' },
  importBaseCatalog: { en: 'Import Base Catalog', es: 'Importar Catálogo Base' },
  importing:       { en: 'Importing…',          es: 'Importando…' },
  selectCompanyForCatalog: { en: 'Select a company to manage the vendor catalog.', es: 'Selecciona una empresa para gestionar el catálogo de proveedores.' },
  emptyCatalog:    { en: 'Empty catalog. Import the base catalog or add vendors.', es: 'Catálogo vacío. Importa el catálogo base o agrega proveedores.' },
  noResultsSearch: { en: 'No results for this search.', es: 'Sin resultados para esta búsqueda.' },
  vendorsInCatalog:{ en: 'vendors',             es: 'proveedores' },
  vendorRuc:       { en: 'RUC (11 digits)',      es: 'RUC (11 dígitos)' },
  vendorName:      { en: 'Company Name',        es: 'Razón Social' },
  pcgeAccount:     { en: 'PCGE Acct.',          es: 'Cta. PCGE' },
  accountDescription: { en: 'Account Description', es: 'Descripción Cuenta' },
  catalogNote:     { en: 'Vendors in this catalog are automatically applied when importing SUNAT XMLs.', es: 'Los proveedores de este catálogo se aplican automáticamente al importar XMLs SUNAT.' },
  encryptExisting: { en: 'Encrypt existing data', es: 'Cifrar datos existentes' },
  migrating:       { en: 'Migrating…',          es: 'Migrando…' },
  deleteVendorConfirm: { en: 'Delete this vendor from the catalog?', es: '¿Eliminar este proveedor del catálogo?' },

  // ── AccountantConsole ────────────────────────────────────────────────
  companies:       { en: 'Companies',           es: 'Empresas' },
  withPending:     { en: 'With pending',        es: 'Con pendientes' },
  completed:       { en: 'Completed',           es: 'Completadas' },
  withErrors:      { en: 'With errors',         es: 'Con errores' },
  activePeriod:    { en: 'Active period',       es: 'Período activo' },
  alerts:          { en: 'Alerts',              es: 'Alertas' },
  actions:         { en: 'Actions',             es: 'Acciones' },
  noCompaniesRegistered: { en: 'No companies registered', es: 'Sin empresas registradas' },
  registerCompany: { en: 'Register company',   es: 'Registrar empresa' },
  goToProfileToRegister: { en: 'Go to Company Profile to register the first client.', es: 'Ve a Perfil Empresa para registrar el primer cliente del despacho.' },
  noResultsFilter: { en: 'No results for the search', es: 'Sin resultados para la búsqueda' },
  adjustFilter:    { en: 'Try adjusting the filter or search.', es: 'Prueba ajustando el filtro o la búsqueda.' },
  cardsView:       { en: 'Cards',              es: 'Tarjetas' },
  tableView:       { en: 'Table',              es: 'Tabla' },
  statusSent:      { en: 'Sent',               es: 'Enviado' },
  statusProcessed: { en: 'Processed',          es: 'Procesado' },
  statusPending:   { en: 'Pending',            es: 'Pendiente' },
  markAsSent:      { en: 'Mark as sent',       es: 'Marcar enviado' },
  unmark:          { en: 'Unmark',             es: 'Desmarcar' },
  registeredCompanies: { en: 'registered companies', es: 'empresas registradas' },

  // ── PyG (Income Statement) ───────────────────────────────────────────
  totalRevenuePyG: { en: 'Total Revenue',      es: 'Total Ingresos' },
  totalExpensesPyG:{ en: 'Total Expenses',     es: 'Total Gastos' },
  netIncomePyG:    { en: 'Net Income',         es: 'Utilidad Neta' },
  netLoss:         { en: 'Net Loss',           es: 'Pérdida Neta' },
  operatingRevenue: { en: 'Operating Revenue', es: 'Ingresos Operacionales' },
  operatingExpenses:{ en: 'Operating Expenses',es: 'Gastos Operacionales' },
  indicators:      { en: 'Indicators',         es: 'Indicadores' },
  netMargin:       { en: 'Net margin',         es: 'Margen neto' },
  expenseLoad:     { en: 'Expense load',       es: 'Carga de gastos' },
  dataSource:      { en: 'Data source',        es: 'Fuente datos' },
  noMovementsPeriod: { en: 'No movements in this period', es: 'Sin movimientos en este periodo' },
  detailAfterProcess: { en: 'Detail available after processing invoices', es: 'Detalle disponible tras procesar facturas' },
  composition:     { en: 'Composition',        es: 'Composición' },

  // ── CierreDual — Balance sections ───────────────────────────────────
  currentAssets:   { en: 'Current Assets',      es: 'Activo Corriente' },
  nonCurrentAssets:{ en: 'Non-Current Assets',  es: 'Activo No Corriente' },
  totalCurrentAssets: { en: 'Total Current Assets', es: 'Total Activo Corriente' },
  totalNonCurrentAssets: { en: 'Total Non-Current Assets', es: 'Total Activo No Corriente' },
  totalAssets:     { en: 'Total Assets',        es: 'Total Activo' },
  currentLiabilities: { en: 'Current Liabilities', es: 'Pasivo Corriente' },
  totalCurrentLiabilities: { en: 'Total Current Liabilities', es: 'Total Pasivo Corriente' },
  fromPyG:         { en: 'from P&L',            es: 'desde P&G' },
  totalEquity:     { en: 'Total Equity',        es: 'Total Patrimonio' },
  totalLiabEquity: { en: 'Total Liabilities + Equity', es: 'Total Pasivo + Patrimonio' },
  operatingRevenueSec: { en: 'Operating Revenue', es: 'Ingresos Operacionales' },
  operatingExpensesSec:{ en: 'Operating Expenses', es: 'Gastos Operacionales' },
  totalRevenueSec: { en: 'Total Revenue',       es: 'Total Ingresos' },
  totalExpensesSec:{ en: 'Total Expenses',      es: 'Total Gastos' },
  profitBeforeParticipation: { en: 'Income Before Profit Sharing & Tax', es: 'Utilidad Antes de Participaciones e IR' },
  profitBeforeIT:  { en: 'Income Before Income Tax', es: 'Utilidad Antes del Impuesto a la Renta' },
  taxableNetIncome:{ en: 'Taxable Net Income',  es: 'Renta Neta Imponible' },
  incomeTax:       { en: 'Income Tax',          es: 'Impuesto a la Renta' },
  totalIncomeTax:  { en: 'Total Income Tax',    es: 'Total Impuesto a la Renta' },
  noTaxNegative:   { en: 'No tax (income ≤ 0 or exempt regime)', es: 'Sin impuesto (utilidad ≤ 0 o régimen exento)' },
  netIncomeFinal:  { en: 'Net Income for the Period', es: 'Utilidad Neta del Ejercicio' },
  transfersToBalance: { en: 'Transfers to Period Results (account 59) in the Balance Sheet', es: 'Se transfiere a Resultados del Ejercicio (cuenta 59) en el Balance' },
  balanceMatches:  { en: '✓ Balance matches',   es: '✓ Balance cuadra' },
  balancePending:  { en: '⚠ Balance pending',   es: '⚠ Pendiente de cuadre' },
  user:            { en: 'User',                es: 'Usuario' },
  additionsNonDeductible: { en: '(+) Additions — Non-deductible Expenses', es: '(+) Adiciones — Gastos No Deducibles' },
  noCurrentAssets: { en: 'No current assets registered', es: 'Sin activos corrientes registrados' },
  noNonCurrentAssets: { en: 'No non-current assets registered', es: 'Sin activos no corrientes registrados' },
  noCurrentLiabilities: { en: 'No current liabilities registered', es: 'Sin pasivos corrientes registrados' },
  noRevenueRegistered: { en: 'No revenue registered',  es: 'Sin ingresos registrados' },
  noExpensesRegistered:{ en: 'No expenses registered', es: 'Sin gastos registrados' },

  // ── DashboardMetrics / no company ────────────────────────────────────
  configureCompanyMetrics: { en: 'Configure your company to view metrics.', es: 'Configura tu empresa para ver las métricas.' },
  configureCompanyMetrics2: { en: 'Configure your company to view metrics', es: 'Configura tu empresa para ver métricas' },

  // ── Sidebar labels (already in Layout.tsx but standardized here) ────
  sidebarLanguage: { en: 'Language',            es: 'Idioma' },
  sidebarDataEncrypted: { en: 'Encrypted data', es: 'Datos cifrados' },
  classificationAI: { en: 'AI Accounting Classification', es: 'Clasificación Contable IA' },
  expandSidebar:   { en: 'Expand sidebar',      es: 'Expandir menú lateral' },
  collapseSidebar: { en: 'Collapse sidebar',    es: 'Contraer menú lateral' },

  // ── Common status badges ─────────────────────────────────────────────
  statusOk:        { en: 'OK',                  es: 'OK' },
  statusError:     { en: 'Error',               es: 'Error' },
  statusWarning:   { en: 'Warning',             es: 'Advertencia' },
  statusInfo:      { en: 'Info',                es: 'Info' },
} as const;

/** Resolved English translations (default, always available) */
export const T: Record<keyof typeof dict, string> = Object.fromEntries(
  Object.entries(dict).map(([k, v]) => [k, v[DEFAULT_LANG]])
) as Record<keyof typeof dict, string>;

/** Runtime translation — pass lang from context */
export function t(key: string, lang: Lang = DEFAULT_LANG): string {
  // Check strongly-typed dict first (short-form keys from index.ts)
  if (key in dict) {
    return (dict as Record<string, Record<Lang, string>>)[key][lang];
  }
  // Fall back to dictionary.ts for dotted keys (e.g. 'libroDiario.title')
  return dictT(key, lang);
}
